import { useState } from 'react';
import { useOutletContext } from 'react-router';
import { ArrowUpRightFromSquare, KeyRound, Lock, Unlock } from 'lucide-react';
import { Button, Card, NotificationCard, useToast } from '@sighthost/ui';
import type { Domain } from '@/domains/domains-registry';
import { domainService } from '@/domains/domains-registry/services/domainService';

export default function DomainTransferTab() {
  const domain = useOutletContext<Domain>();
  const { toast } = useToast();
  const [emailing, setEmailing] = useState(false);

  if (domain.status === 'transfer-in') {
    return (
      <NotificationCard
        tone="info"
        title="Transfer in progress"
        body={
          <>
            We are transferring <strong>{domain.name}</strong> from its previous registrar.
            The previous owner needs to approve the transfer at their losing registrar.
            Most transfers complete within 5–7 days.
          </>
        }
      />
    );
  }

  async function emailEppCode() {
    setEmailing(true);
    try {
      await domainService.sendEppCode(domain.id);
      toast({
        tone: 'success',
        title: 'EPP code emailed to registrant',
        body: 'Check the registrant inbox — the code is single-use and expires after the transfer.',
      });
    } catch (e) {
      toast({
        tone: 'danger',
        title: 'Could not email the EPP code',
        body: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setEmailing(false);
    }
  }

  const eppDisabledReason = domain.registryLocked
    ? 'Unlock the domain first (step 1).'
    : null;

  return (
    <div className="space-y-6">
      <Card interactive={false}>
        <header className="border-b border-border px-6 py-3">
          <h3 className="text-sm font-semibold text-ink inline-flex items-center gap-2">
            <ArrowUpRightFromSquare className="size-4" aria-hidden />
            Transfer out
          </h3>
          <p className="text-xs text-muted-foreground">
            Move <strong>{domain.name}</strong> to another registrar.
          </p>
        </header>

        <div className="space-y-4 px-6 py-5">
          <Step
            n={1}
            done={!domain.registryLocked}
            title="Unlock the domain"
            body={
              domain.registryLocked
                ? 'The registry lock prevents unauthorised transfers. Open a support ticket to unlock.'
                : 'Domain is unlocked.'
            }
            icon={domain.registryLocked ? Lock : Unlock}
          />
          <Step
            n={2}
            title="Get the EPP / auth code"
            body="The auth code authenticates the transfer at the gaining registrar. We email it to your registrant address — never displayed in the portal."
            icon={KeyRound}
            actions={
              <Button
                variant="outline"
                size="sm"
                disabled={domain.registryLocked || emailing}
                loading={emailing}
                onClick={emailEppCode}
                title={eppDisabledReason ?? undefined}
              >
                Email me the code
              </Button>
            }
          />
          <Step
            n={3}
            title="Initiate the transfer at the gaining registrar"
            body="Use the auth code at the new registrar. They will email the registrant for approval. Transfers typically complete within 5–7 days for .co.za and gTLDs."
          />
        </div>
      </Card>

      <NotificationCard
        tone="warning"
        title="Transfer eligibility"
        body="Transfers within 60 days of registration or last transfer are blocked by ICANN / ZACR rules. Domain expiry should be more than 30 days away."
      />
    </div>
  );
}

function Step({
  n,
  title,
  body,
  icon: Icon,
  actions,
  done,
}: {
  n: number;
  title: string;
  body: string;
  icon?: React.ComponentType<{ className?: string }>;
  actions?: React.ReactNode;
  done?: boolean;
}) {
  return (
    <div className="flex gap-4">
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums ${
          done ? 'bg-success/10 text-success' : 'bg-muted text-foreground-soft'
        }`}
      >
        {n}
      </div>
      <div className="flex-1 space-y-2">
        <p className="text-sm font-semibold text-ink">
          {Icon && <Icon className="mr-1.5 inline h-3.5 w-3.5 text-muted-foreground" />}
          {title}
        </p>
        <p className="text-sm text-foreground-soft">{body}</p>
        {actions}
      </div>
    </div>
  );
}
